/**
 * The hedgerow — beat 7's route out, and the exterior shared by later beats.
 *
 * `hedgerowGroundY` is deliberately the only statement of this room's ground.
 * It ignores X today because the signature is the seam later cross-falls need;
 * changing that future terrain must not require every placement to change API.
 * Shell meshes and colliders come from `hedgerowSolids()` together. This is
 * particularly important for a shallow slope: a few centimetres of disagreement
 * is invisible in a frame but makes Rosalie float or catch her feet.
 */

import * as THREE from 'three';
import { mulberry32 } from '@/audio/prng';
import { BOX_TRAILER_RUNNING_GEAR } from '@/models/env/box-trailer';
import { CULVERT_DIMENSIONS } from '@/models/env/culvert-headwall';
import { buildFenceBoards } from '@/models/env/fence-boards';
import { buildHedgeSection } from '@/models/env/hedge-section';
import { buildPalletStack } from '@/models/env/pallet-stack';
import { surface } from '@/models/materials';
import { getAsset } from '@/models/registry';
import type { AssetDefinition } from '@/models/types';
import type { CompanionObstacle } from '@/systems/companion';
import type { ZoneLightingOptions } from '@/render/lighting';
import { chromaForBeat, getZone, type ZonePalette } from '@/render/palette';
import { createSky, type Sky } from '@/render/sky';
import { instanceGroup, type Placement } from '@/world/instancing';
import { solidMeshes, type HallSolid } from '@/world/solids';

export const HEDGEROW_ZONE_ID = 'yard';
export const HEDGEROW_BLOOM_ZONE_ID = 'outside';
/** The beat this room's night belongs to, and the row its grade ceiling comes from. */
const NIGHT_BEAT = 10;

const FALL_GRADE = 0.04;
const FIELD_GRADE = 0.015;
const GROUND_THICKNESS = 0.6;
const FENCE_HEIGHT = 2.15;
/**
 * Half-height of the room's invisible perimeter. See `bound-west` below.
 *
 * 3.5 m each way from y = 0 clears the terrain at both ends by a margin: the
 * lowest floor in the room is the ditch bed's underside at −1.92 and the field's
 * far end at −1.80, and the tallest thing she can arrive with is a roll at the
 * 4.5 m/s cap, which is nowhere near 3.5 m of launch. A boundary she can clear
 * is not a boundary, and one that stops at the terrain's nominal height is the
 * bug the fence bay already taught this project (a hole 0.31 m off the ground is
 * a window, not a hole).
 */
const BOUND_HALF_HEIGHT = 3.5;
const BOUND_HALF_THICKNESS = 0.5;
const BOUND_SIDE_NORTH_OVERLAP = BOUND_HALF_THICKNESS;
const BOUND_SIDE_SOUTH_OVERLAP = 1;

/**
 * The three INNER faces where the player actually stops; the far hedge is the
 * already-visible south bound. The invisible colliders extend outward from these
 * lines by `BOUND_HALF_THICKNESS`, while the visible field fence sits on them.
 * These used to be collider centre lines, which placed the fence half a metre
 * beyond the floor edge — including over the ditch void on the east. Both the
 * containment solids and the visible field fence read this one statement, so
 * they cannot drift apart again. `hedgerow-boundary.spec.ts` derives its samples
 * from this object and makes that drift fail.
 */
export const HEDGEROW_BOUNDS = {
  westX: -17,
  eastX: 14,
  northZ: 11,
  southZ: -86,
} as const;

/** The wide post spacing that keeps the field edge open rather than compound-like. */
export const HEDGEROW_STOCK_FENCE_PITCH = 4;

/** THE single statement of where the ground is. See docs/hedgerow-layout.md. */
export function hedgerowGroundY(_x: number, z: number): number {
  if (z >= 0) return 0;
  if (z >= -18) return -FALL_GRADE * Math.abs(z);
  if (z >= -18.5) return -0.72 - (Math.abs(z) - 18) * 1.2;
  if (z >= -19.9) return -1.32;
  if (z >= -20.4) return -1.32 + (Math.abs(z) - 19.9);
  return -0.82 - FIELD_GRADE * (Math.abs(z) - 20.4);
}

export const HEDGEROW_SPAWN = {
  x: 3,
  y: hedgerowGroundY(3, 8),
  z: 8,
  facing: Math.atan2(-4, 1.8),
} as const;

/**
 * Beat 7's one opening in the field fence, and beat 10's front door.
 *
 * It is declared up here rather than beside `BOARDS_BREAK_SPEED` because
 * `HEDGEROW_NIGHT_SPAWN` is derived from it and a `const` cannot be read above
 * its own declaration. The two belong together: this x is the whole reason that
 * spawn is where it is.
 */
export const BOARDS_AT = { x: 3, z: 0, rotY: Math.PI / 2 } as const;

/**
 * The plane she is through the field fence at: beat 7's `pastTheFence`, beat
 * 10's `inTheField`, and where beat 10's goals stop pointing at the bay. The
 * fence is at z = 0; this is her body radius and a little past it, so a snout
 * pressed into unbroken boards is not "through".
 */
export const PAST_FIELD_FENCE_Z = -0.35;

/**
 * Beat 10 starts on the clear apron rather than under the trailer, already facing
 * down the fall. The later beat must not reuse Beat 7's under-deck continuity
 * spawn: a player who begins inside the trailer has no way to read the moonlit
 * route before the first movement.
 *
 * ── It is on `BOARDS_AT`'s line, and that is the whole opening (D-173) ───────
 *
 * This was (0, 6) for three stages, and **from there the beat was unreachable**:
 * the field fence runs the width of the room at z = 0 and its one opening is the
 * timber bay at x = 3. Holding forward from (0, 6) stopped her at z = 0.19 and
 * left her there; rolling the bay at `rollMaxSpeed` stopped her at z = 0.26,
 * because beat 7's speed check belongs to beat 7's director and nothing in beat
 * 10 watches those boards. The twelve, the dog, the culvert and every downfield
 * call are south of that line. Four specs appeared to play the beat and every one
 * of them had teleported past it with `respawn`.
 *
 * So the spawn is **six metres due north of the bay she broke in beat 7**, which
 * is now built `'broken'` and has no collider (`HedgerowOptions.boards`). Holding
 * forward walks her through her own hole and into the field — measured, z = −9.0
 * after eight seconds — and the establishing frame of Act 2's biggest beat is the
 * gap she made getting out, seen from the end she made it from. The trailer she
 * launched that roll from is 2.6 m behind her at (3, 8.6).
 *
 * **The huddle is no longer in the opening frame and that is the trade.** From
 * here it is 15.53 m away and 33° off her nose, at the edge of the lens; through
 * the gap it is 10.40 m away and 50° off. The count is therefore taken in the
 * field, after a look around, rather than handed over before the first step —
 * which is what spec §7.10's *"visible only as a diegetic huddle you can look
 * at"* asks for anyway. See `HERD_HUDDLE_AT`, whose own derivation this replaces.
 */
export const HEDGEROW_NIGHT_SPAWN = {
  x: BOARDS_AT.x,
  y: hedgerowGroundY(BOARDS_AT.x, 6),
  z: 6,
  facing: Math.PI,
} as const;
/**
 * Where Holly lies up at the start of beat 10, and where `F` sends her back to.
 *
 * ── It is on the FARM side of the fence, and the geometry leaves no choice ───
 *
 * Session 50 moved it to the field side, (5.5, −1.5), so that Holly and the beat
 * were on one side of the wall she had until then been walking through. That is
 * a coherent-sounding placement and **it broke stage 3**, in two ways a frame
 * would not have shown:
 *
 *  1. **The direction of the draw is the mechanic.** `F`'s second leg sends her
 *     back, the bell recedes toward this point, and the dog follows it — so this
 *     point decides *which way the dog is pulled off its post*. Due north of
 *     `NIGHT_DOG_AT` walks it straight up the field with its back to the
 *     channel, which is what makes the channel cover (D-167). North-EAST walked
 *     it round until it could see her in the channel, and it drove.
 *  2. **It cannot be both due north of the dog and out of earshot on the field
 *     side.** The field between the fence at z = 0 and the post at z = −17.2 is
 *     17.2 m deep and the bell carries 18.90 m. There is no point at x ≈ −9,
 *     south of the fence, that is far enough away. The constraint is arithmetic,
 *     not taste.
 *
 * So it is back at (−9.4, 8.0): **25.20 m from the dog** against the 18.90 m
 * bell radius, and 0.4 m off due north of it. z = 8.0 clears the near bin's
 * body-inflated 8.79…10.01 footprint by 0.79 m; 8.6 missed it by 19 mm. It is
 * 12.56 m from `HEDGEROW_NIGHT_SPAWN`, which is longer than the 9.61 m the old
 * spawn gave and is why three specs that waited out a hand-computed journey went
 * red.
 *
 * **What this costs is that Holly has to cross the fence, and `Companion` cannot
 * route.** `#moveToward` slides along an obstacle and takes the first clear axis;
 * a companion asked to negotiate a 31 m wall with one 2 m hole solves it only
 * when the slide happens to point at the hole, which is true northbound and
 * false southbound. `TwelveOut` therefore aims her at the gap first — see
 * `CompanionGap` there — and the fence runs stay in
 * `hedgerowCompanionObstacles()` as the backstop that makes that routing
 * necessary rather than decorative.
 */
export const HOLLY_LIE_UP = { x: -9.4, z: 8.0, yaw: Math.PI * 0.75 } as const;
/** The dog on the near bank, watching the fall; ground is -0.688 m. Stage 3 gives it legs. */
export const NIGHT_DOG_AT = { x: -9.0, z: -17.2, facing: 0 } as const;


/**
 * How fast she has to arrive to go through the timber, m/s.
 *
 * Beat 7's rule 2: *the boards are a wall until she is fast, and nothing says so
 * in words.* `nudge` and `root` are offered on this bay and both fail (D-043);
 * what opens it is speed, and the only way to be fast is to roll.
 *
 * ── The derivation, INCLUDING fatigue, because the first one omitted it ──────
 *
 * All from `src/physics/character.ts`'s `ROSALIE_TUNING`. On the flat, with input
 * held, `#updateHorizontalVelocity` adds `rollAccel` and then subtracts `drag`
 * as a **constant** deceleration (`slowed = max(0, rollSpeed - drag * dt)`), so
 * the net is a plain acceleration and not a proportional-drag ODE:
 *
 *     drag(t)  = rollDrag 1.35 + 4.5 · clamp((t − rollFatigueDelay 1.5) / rollFatigueRamp 2, 0, 1)
 *     a(t)     = rollAccel 4.2 − drag(t)
 *     a(t)     = 2.85           for t < 1.5
 *              = 2.85 − 2.25·(t − 1.5)   for 1.5 ≤ t < 3.5   → reaches 0 at t = 2.767
 *              = −1.65          for t ≥ 3.5
 *
 * She enters the roll from a trot (`rollMinEntrySpeed` 1.35 forbids entering from
 * a standstill, `trotSpeed` is 1.9), and 8.0 m is the gap from under the trailer
 * at (3, 8.6) to the fence at z = 0:
 *
 *     reaches the 4.5 m/s cap at   (4.5 − 1.9) / 2.85 = 0.912 s, after 2.92 m
 *     coasts at the cap to t = 1.5                              +2.65 m → 5.56 m
 *     still at the cap at 8.00 m                    t ≈ 2.04 s
 *
 * **Fatigue does not bite before she arrives.** It starts at t = 1.5 s and does
 * not cancel the acceleration until t = 2.767 s, and she is at the fence at
 * t ≈ 2.04 s — still pinned to `rollMaxSpeed`. So the reachable arrival speed is
 * **4.5 m/s**, the cap itself, and the threshold is 75 % of it.
 *
 * That is worth the paragraph because the first implementation solved the roll
 * without the fatigue terms *and* without the speed cap and got 3.075 m/s, which
 * would have put the threshold at 2.306 — **below what a plain trot plus a shove
 * can produce**, and a fence that opens for a brisk walk teaches nothing. The
 * error was in the safe direction by luck, not by design.
 *
 * 75 % rather than 100 % because the cap is what a *perfect* line reaches and this
 * has to open for an ordinary one: `out-run.spec.ts` drives it twice, once
 * steering badly, and both runs must get through. **A trot is 1.9 m/s, so no
 * amount of walking into it can ever be enough** — which is the whole of what the
 * geometry has to teach.
 *
 * If a stopwatch says an ordinary player cannot break through, **lower this
 * number. Do not add a prompt.**
 */
export const BOARDS_BREAK_SPEED = 3.375;

export const DITCH_AT = {
  minX: -22,
  maxX: 14,
  nearBankZ: -18,
  /**
   * The channel's two edges, named by which BANK they are at and not by min/max.
   *
   * They were `channelMinZ: -18.5` and `channelMaxZ: -19.9`, named by |z|, and
   * that is backwards in world space: −18.5 is the LARGER z. `hedgerowCover()`
   * duly built an AABB with `min.z = -18.5, max.z = -19.9`, and
   * `bestConcealment()` tests `z >= min.z && z <= max.z` — which is satisfiable
   * by nothing. **The ditch's cover volume was empty**, so the one place in the
   * beat where the dogs are supposed to lose her concealed her not at all, and
   * rule 1's samples in the water were unsheltered.
   *
   * It typechecked, the room booted, and no gate that exists would have caught
   * it. The rename is the fix: a field called `channelMinZ` invites being written
   * into a `min`, and the next author would have made the same inversion.
   */
  channelNearZ: -18.5,
  channelFarZ: -19.9,
  farBankZ: -20.4,
  bedY: -1.32,
  waterY: -1.14,
} as const;
/**
 * Where the drain leaves the farm, and beat 10's one landmark.
 *
 * `DITCH_AT` spans x −22 … +14 while `HEDGEROW_BOUNDS.westX` stops at −17, so
 * this room has always modelled the channel five metres past its own perimeter:
 * the water visibly goes somewhere and an invisible collider is the only thing
 * that stops her following it. **The hole in the perimeter was already there in
 * the fiction and in the geometry** — see `docs/beat-10-plan.md`, which records
 * that the obvious alternative (sink the far field, put a culvert under the far
 * hedge) cannot be built, because `hedgerowGroundY` is the one statement of the
 * ground and lowering it would lower beat 7's field under beat 7's held camera.
 *
 * The headwall's centre plane is 0.1 m inside `bound-west`'s inner face, so the
 * barrel's buried half disappears into the collider rather than ending in
 * mid-air, and its mouth stands 0.4 m proud on the approach side. The asset's +Z is
 * the APPROACH — the face she sees — so it maps to world +X, back up the
 * channel toward the farm. Hence the quarter turn, and its sign.
 */
export const CULVERT_AT = {
  x: -16.9,
  z: (DITCH_AT.channelNearZ + DITCH_AT.channelFarZ) / 2,
  y: DITCH_AT.bedY,
  rotY: Math.PI / 2,
} as const;

/**
 * The plane a later stage ends the beat on, and it is inside the pipe.
 *
 * **`bound-west` is what actually stops her, not the culvert**, and that governs
 * this number. Its inner face is at x = −17 and the headwall's centre plane is at
 * −16.9, so the reachable pipe is about half a metre long and her body radius
 * eats a third of it. Measured, from the channel bed driving −X:
 *
 *   - **down the bore she stops at x = −16.86**, which is 0.5 m past the mouth
 *     plane at −16.36 and visibly inside the barrel;
 *   - **at the jamb, 0.45 m off the channel's centre line, she stops at −16.62**,
 *     because there the concrete is a full 1.1 m out of the bed and she cannot
 *     climb it.
 *
 * −16.75 sits between the two with about 0.11 m either side. Firing the end of
 * the beat at the mouth itself would cut on the frame she touches the rim; here
 * she is a body length in. `beat-10-night.spec.ts` asserts both runs against it
 * and that is the delta — one number cannot say a hole is a hole.
 *
 * Nothing consumes it in stage 1. It is exported so it is authored beside the
 * geometry it is derived from rather than by the stage that first needs it.
 */
export const CULVERT_ENTER_X = -16.75;

/**
 * Where the twelve stand at the start of beat 10, and it is chosen from three
 * sight lines rather than from the middle of the field.
 *
 * **The sight line this was chosen from no longer exists** (D-173). It read:
 * *"`HEDGEROW_NIGHT_SPAWN` is (0, 6) facing −Z, so this is 14.12 m ahead and 23°
 * off her nose: she can see them from the first frame without turning."* Both
 * halves were true and the conclusion was worthless, because that line ran
 * through the field fence — the counting shot every session argued about
 * (`06-the-huddle-from-the-spawn.png`) is twelve animals behind wire mesh, and
 * *"they do not resolve as twelve at 14 m"* was a diagnosis of the fence.
 * Unobstructed at 10.40 m they read as a herd. The frames are in
 * `docs/verification/beat10/ladder/`.
 *
 * The placement is kept, and it is now justified by where she actually is when
 * she can see it: **10.40 m from the gap at (3, 0)**, 50° off the nose she comes
 * through it with, so the count is something she turns her head for. It is also
 * above the fall — ground here is −0.28 m and the descent proper does not start
 * until she is past them — so the huddle reads against grass rather than against
 * the skyline, and twelve separate backs stay separable.
 *
 * The third sight line is the dog's. `NIGHT_DOG_AT` is **10.78 m** away at the
 * top of the bank, which puts it between the huddle and the way down without
 * being near enough to be the first thing the player deals with. (12.28 m stood
 * here for one commit and is not the distance between those two points.)
 */
export const HERD_HUDDLE_AT = { x: -5.5, z: -7.0 } as const;

/**
 * The route the herd walks, huddle to pipe, and **it is the room's statement of
 * it rather than the queue's.** `src/story/` does not import `src/world/`, so
 * `CulvertQueue` knows one number — how long this is — and the projection below
 * is what turns Rosalie's position into a place on it.
 *
 * Four points, and each one is a decision:
 *
 *  1. **the huddle**, `HERD_HUDDLE_AT`;
 *  2. **the top of the bank at (−13, −15)**, which is where the fall runs out.
 *     It is 4.42 m from the dog's post at its closest — near enough that driving
 *     twelve animals past it is the trade the beat is about, far enough that the
 *     string is not walking through the animal;
 *  3. **the channel at (−15, −19.2)**, which is `CULVERT_AT.z` exactly. Turning
 *     into the bed here rather than at the mouth is what lines the string up
 *     with the bore instead of with the headwall's shoulders;
 *  4. **the mouth plane at (−16.36, −19.2)**, the number `CULVERT_ENTER_X`'s own
 *     derivation names as the face of the pipe. The last 0.39 m into the barrel
 *     is Rosalie's at the end of the beat and is not the herd's route.
 *
 * **The y is not in here on purpose.** `hedgerowGroundY` is the one statement of
 * where the ground is (D-056/D-059) and a route that carried its own heights
 * would be a second one; the renderer looks each point up. Stage 5's run reuses
 * this list and must not fork it.
 */
export const CULVERT_ROUTE = [
  { x: HERD_HUDDLE_AT.x, z: HERD_HUDDLE_AT.z },
  { x: -13.0, z: -15.0 },
  { x: -15.0, z: CULVERT_AT.z },
  { x: -16.36, z: CULVERT_AT.z },
] as const;

/**
 * The route's length in metres, summed rather than written down.
 *
 * 16.978 m as authored. It is computed because it is the number
 * `CulvertQueue` is constructed with and the number every expected arrival time
 * in the spec is derived from: a literal here and a moved waypoint there is a
 * queue whose animals arrive at a place that is no longer the pipe, and nothing
 * in the frame would say so. `docs/traps.md`: derive an expected value from the
 * exported constant the code reads, never from arithmetic done in your head.
 */
export const CULVERT_ROUTE_LENGTH = ((): number => {
  let total = 0;
  for (let index = 1; index < CULVERT_ROUTE.length; index += 1) {
    const from = CULVERT_ROUTE[index - 1];
    const to = CULVERT_ROUTE[index];
    if (!from || !to) throw new Error('culvert route: the polyline is not contiguous');
    total += Math.hypot(to.x - from.x, to.z - from.z);
  }
  return total;
})();

/** Where a point sits on `CULVERT_ROUTE`: how far along, and how far off. */
export interface CulvertRoutePlace {
  /** Metres from the huddle along the polyline, clamped to its length. */
  readonly distance: number;
  /** Perpendicular metres from the nearest segment. */
  readonly offset: number;
}

/**
 * Project a world position onto the route.
 *
 * **This is the whole of what the queue knows about the room**, and the split is
 * the reason the queue replays in Node with no Three in the process. It is the
 * nearest point on any segment, not on the nearest segment's infinite line: the
 * polyline turns **17.7° at the bank and 64.5° into the channel**, and an
 * unclamped projection at a corner reports a distance the herd has not walked.
 * (This said 26° and 17°, which are respectively nothing and the bank's turn
 * misattributed to the channel; a review recomputed them from `CULVERT_ROUTE`
 * itself. The real 64.5° makes the argument for clamping stronger, not weaker.)
 *
 * Called once per fixed step with Rosalie's position and nothing else.
 */
export function culvertRoutePlace(x: number, z: number): CulvertRoutePlace {
  let bestOffset = Infinity;
  let bestDistance = 0;
  let travelled = 0;
  for (let index = 1; index < CULVERT_ROUTE.length; index += 1) {
    const from = CULVERT_ROUTE[index - 1];
    const to = CULVERT_ROUTE[index];
    if (!from || !to) throw new Error('culvert route: the polyline is not contiguous');
    const dx = to.x - from.x;
    const dz = to.z - from.z;
    const length = Math.hypot(dx, dz);
    // A zero-length segment would divide by zero and is a waypoint typed twice.
    if (length === 0) throw new Error('culvert route: a segment has no length');
    const along = Math.max(0, Math.min(length, ((x - from.x) * dx + (z - from.z) * dz) / length));
    const offset = Math.hypot(x - (from.x + dx / length * along), z - (from.z + dz / length * along));
    if (offset < bestOffset) {
      bestOffset = offset;
      bestDistance = travelled + along;
    }
    travelled += length;
  }
  return { distance: bestDistance, offset: bestOffset };
}

/** A place on the route, in the room's own coordinates. `heading` is a yaw. */
export interface CulvertRoutePoint {
  readonly x: number;
  readonly z: number;
  /** The direction of travel at that point, radians, `atan2(dx, dz)`. */
  readonly heading: number;
}

/**
 * The inverse of `culvertRoutePlace`: metres along the route to a place in the
 * room. What the herd's renderer reads, once per animal per frame.
 *
 * Off either end it returns the terminal waypoint with the terminal heading
 * rather than extrapolating. Extrapolation past the mouth would walk a pig into
 * `bound-west`; past the huddle it would walk one back up the field, and the
 * queue's own contract already says distances never decrease.
 */
export function culvertRoutePointAt(distance: number): CulvertRoutePoint {
  let remaining = Math.max(0, Math.min(CULVERT_ROUTE_LENGTH, distance));
  for (let index = 1; index < CULVERT_ROUTE.length; index += 1) {
    const from = CULVERT_ROUTE[index - 1];
    const to = CULVERT_ROUTE[index];
    if (!from || !to) throw new Error('culvert route: the polyline is not contiguous');
    const dx = to.x - from.x;
    const dz = to.z - from.z;
    const length = Math.hypot(dx, dz);
    const heading = Math.atan2(dx, dz);
    if (remaining <= length || index === CULVERT_ROUTE.length - 1) {
      const along = Math.min(remaining, length);
      return { x: from.x + dx / length * along, z: from.z + dz / length * along, heading };
    }
    remaining -= length;
  }
  throw new Error('culvert route: the polyline has no segments');
}

/**
 * ── Where the twelve stop while the dog is home (D-184) ──────────────────────
 *
 * Stage B of `docs/beat-10-plan.md` says *"a dog works the ground between the
 * huddle and the route down"*, and for four stages nothing enforced the second
 * half of that: the dog threatened Rosalie and the string walked past it at four
 * metres as though it were scenery. D-180 measured what that cost — the same
 * descent scored `10 through, 10 out` called and uncalled — because
 * `CulvertQueue` did not know the animal existed. These four are what it knows
 * now, and every one of them is COMPUTED from the route and the post rather than
 * typed, so a moved waypoint moves the barrier with it.
 *
 * How far short of the crossing the string stops, metres.
 *
 * The route's closest approach to the post is 4.42 m and it runs almost
 * tangentially past it, so a standoff buys nearly nothing in separation; what it
 * buys is the picture — the animals stop *before* the dog's ground rather than
 * drawing level with it and halting beside the thing they will not pass.
 *
 * **It is not free to raise.** Past about three metres the head is pinned far
 * enough back that Rosalie standing at the pipe mouth is outside `DRAW_RANGE`,
 * and the beat's one voice would then tell the player they abandoned the animals
 * when what actually happened is that a dog is in the way. `main.ts` throws at
 * module load rather than leaving that to this comment.
 */
export const HERD_BALK_STANDOFF = 2.0;
/** The post's own place on the route the twelve walk: 9.835 m along, 4.423 m off. */
export const HERD_BALK_PLACE = culvertRoutePlace(NIGHT_DOG_AT.x, NIGHT_DOG_AT.z);
/** That, less the standoff — the greatest route distance the head may reach while the dog is home. */
export const HERD_BALK_DISTANCE = HERD_BALK_PLACE.distance - HERD_BALK_STANDOFF;
/** The same place in the room, so presentation never projects a second route to find it. */
export const HERD_BALK_POINT = culvertRoutePointAt(HERD_BALK_DISTANCE);
/**
 * How far off its post the dog must be before the way down is open, metres.
 *
 * **It is the distance from the post to the balk point, and that is the whole
 * definition**: the dog guards the crossing while it is no further from home
 * than the crossing is, so *"off its post"* and *"out of the way"* are one fact
 * and one number rather than two that drift apart the first time a waypoint
 * moves. `NightDog.distanceFromPost()` is already the instrument this beat
 * reasons with (D-167, D-180), and the bell grows it monotonically as Holly is
 * drawn north — which is what makes the block clear once rather than chatter.
 */
export const DOG_POST_GUARD_RADIUS = Math.hypot(HERD_BALK_POINT.x - NIGHT_DOG_AT.x, HERD_BALK_POINT.z - NIGHT_DOG_AT.z);

export const HEDGE_GAP_X = -6;

/**
 * Where she comes up through the hedge, which is where stage 5's camera is aimed.
 *
 * **There is deliberately no `FIELD_MARK` beside it.** This was one, at
 * (−3, −74), meant as the spot stage 7 stops her on — and a fixed mark is the
 * wrong shape for that stage twice over: stage 6 ends when she crosses one of two
 * thresholds a long way apart, so a fixed mark would teleport her *backwards*
 * from wherever she got to; and the whole point of rule 4 is that the field has no
 * place she is supposed to be. `OutDirector` frames the last shot from **her own
 * position** at the moment the cue fires, and the sight line over the hedgerow to
 * the shed holds across the whole field. See `LOOK_BACK_CAMERA` there.
 */
export const HEDGE_GAP_AT = { x: HEDGE_GAP_X, z: -20.9 } as const;

/**
 * The trailer she starts beat 7 under, and its deck's footprint in world X/Z.
 *
 * Beat 6's `isUnderTrailer` cannot be reused: that trailer is parked 0.35 rad off
 * square (D-108) and the test is written in the unit's own axes. **This one is
 * square on** — its long axis is world X — so the test is an axis-aligned box, and
 * writing it as one here is honest rather than lazy. If this trailer is ever
 * re-parked askew, this function is the place that has to grow, and beat 6 already
 * contains the worked example.
 *
 * It is what raises `OUT_FLAGS.leftTheTrailer`, so it is the end of stage 1: the
 * flag says *the world has finished putting her outside the deck's shadow*, which
 * is a fact about a room and not about a keypress (D-090).
 */
export function isUnderTrailerHedgerow(x: number, z: number): boolean {
  const trailer = assetDefinitionOrThrow('box-trailer').footprint;
  return Math.abs(x - TRAILER_AT.x) <= trailer.length / 2
    && Math.abs(z - TRAILER_AT.z) <= trailer.width / 2;
}

/** Where that trailer stands. Exported so the director's cameras stop restating it. */
export const TRAILER_AT = { x: 3, z: 8.6 } as const;

/**
 * Beat 7's square-on conversion of `BOX_TRAILER_RUNNING_GEAR` to world axes.
 *
 * `rotY = π/2` maps the asset's local Z (its long axis) onto world X, so the
 * local X/Z axes deliberately swap here. The deck remains one overhead box, but
 * the running gear must stay individual: a broad AABB through all four tyres or
 * both legs draws a passage Rosalie cannot actually use.
 */
const TRAILER_LOCAL = BOX_TRAILER_RUNNING_GEAR;

export type HedgerowSolid = HallSolid;

const zone: ZonePalette = (() => {
  const found = getZone(HEDGEROW_ZONE_ID);
  if (!found) throw new Error('hedgerow: yard zone missing from palette');
  return found;
})();

function zoneSurface(index: number): number {
  const colour = zone.surfaces[index];
  if (colour === undefined) throw new Error(`hedgerow: surface ${index} missing from yard palette`);
  return colour;
}

/**
 * The farm's surfaces come from the `yard` zone; the FIELD's come from `outside`.
 *
 * ── Why this is two palettes and not one, and it was one ─────────────────────
 *
 * A material's albedo is baked at `surface()` time and **never changes when the
 * zone changes** — `applyZone()` writes the light, the fog and the grime, and
 * nothing else. `docs/beat-7-plan.md` rule 3 says so in as many words: *"the
 * grass, the hedge and she herself keep the material they were built with. That
 * is correct and is why the field must be AUTHORED in the outside palette's
 * surface colours rather than expected to arrive there."*
 *
 * The first build of this room took every albedo from `HEDGEROW_ZONE_ID`, which
 * is `yard`. So `EARTH` — the fall, both ditch banks and seventy metres of field
 * — was `0x121a29`, the yard zone's **shadow** value and the darkest number in
 * that palette. The result was a black frame with a few pale grass blades in it:
 * the tufts are authored in their own greens and lit correctly, so they read,
 * standing on ground that took no light at all. And it would have stayed black
 * *after* the bloom, because the bloom cannot reach an albedo.
 *
 * So the split is by **what a thing is**, not by which zone the room boots in:
 *
 * - the apron, the shed wall and the steel are the FARM, seen from its far side.
 *   They keep the yard's greys, and they are what the timber bay has to look
 *   wrong against.
 * - the fall, the banks, the field and the ditch's earth are OUTSIDE. They are
 *   the first thing in the game painted in the palette the game has been
 *   draining toward for six beats.
 *
 * Which is also the answer to *"why is the room's zone `yard` if half of it is
 * painted `outside`?"* — the zone is the LIGHT, and at the fence the light is
 * still the storm's. The ground was always going to be grass.
 */
const bloomZone: ZonePalette = (() => {
  const found = getZone(HEDGEROW_BLOOM_ZONE_ID);
  if (!found) throw new Error('hedgerow: outside zone missing from palette');
  return found;
})();

function bloomSurface(index: number): number {
  const colour = bloomZone.surfaces[index];
  if (colour === undefined) throw new Error(`hedgerow: surface ${index} missing from outside palette`);
  return colour;
}

const APRON = zoneSurface(1);
const SHED_WALL = zoneSurface(2);
const STEEL = zoneSurface(3);
/** Wet turf over clay. The `outside` zone's own darkest green, not the yard's. */
export const HEDGEROW_EARTH = bloomSurface(0);
const EARTH = HEDGEROW_EARTH;
/** The road, and it is the one man-made thing on the far side. Tarmac, not grass. */
export const HEDGEROW_ROAD = zoneSurface(1);
const ROAD = HEDGEROW_ROAD;

/**
 * The zenith of beat 7's sky: the `outside` zone's own `fill`, taken down hard.
 *
 * `ZonePalette.fill` is documented as *"bounce and sky fill"* — it is the sky's
 * colour by definition, and `0x8fc0d9` is a pale dawn blue. `createSky` defaults a
 * zenith to the zone's `shadow` instead, which is right everywhere else and wrong
 * here: `outside`'s shadow is `0x3a5742`, a dark **green**, because in a field the
 * deepest value in frame is shadow on grass. Baked straight in it gave the bloom a
 * flat blue-grey sky with a green cast overhead and no dawn in it at all.
 *
 * 0.30 rather than a hand-picked hex, so this **darkens a palette value instead of
 * inventing one** — the difference between rendering the palette and copying it,
 * which is the rule the mood board exists to keep. If the colour arc ever moves
 * `outside`'s fill, this moves with it.
 */
export const DAWN_ZENITH = new THREE.Color(bloomZone.fill).multiplyScalar(0.3).getHex();

/**
 * Beat 10's light is the existing outside palette at night, not a second
 * registered palette. Every changed colour darkens the outside sky fill or its
 * shadow by a scalar, which is the same rule as `DAWN_ZENITH`: rendering the
 * palette stays possible when its source values move, while copied hexes drift.
 *
 * `haze` is the one taste call here. Night needs one distance lever so the far
 * hedge can dissolve into the brighter horizon; the room's `HEDGEROW_FOG_SCALE`
 * remains the shared physical scale rather than becoming a second night tuning.
 */
export function hedgerowNightZone(): ZonePalette {
  const outside = getZone(HEDGEROW_BLOOM_ZONE_ID);
  if (!outside) throw new Error('hedgerow: outside zone missing from palette');
  return {
    ...outside,
    id: 'outside-night',
    // ── Moonlight is the sky's own colour, TAKEN DOWN ─────────────────────
    //
    // Reflected sky light, so `outside.fill` is the right hue and the wrong
    // value: it is a pale dawn blue, and a surface fully lit by it is not dark.
    //
    // **The first three builds tried to get night out of exposure instead**, at
    // key intensities of 0.30, 0.40 and 0.62 against a reference well above the
    // sum, and every one of them came back as a single flat value with edge lines
    // on it. The reason is in the ramp: `TOON_RAMP` has three soft bands, its top
    // one does not begin until irradiance approaches the reference, and pushing a
    // whole scene below that compresses every surface in it into the same dark
    // band. A probe at `keyIntensity: 3.0` modelled the culvert perfectly and
    // proved the light was arriving all along.
    //
    // So the ramp gets its full range — intensities near the reference — and the
    // NIGHT comes from the light being a dark colour. That is also the physical
    // reading: a moonlit field is not a dim white light, it is a full shading
    // range in a colour with almost nothing in it.
    key: outside.fill,
    fill: new THREE.Color(outside.fill).multiplyScalar(0.30).getHex(),
    // **The night's deepest value comes from the SKY, not from the grass.**
    // The first build derived it from `outside.shadow` — a dark green, correct at
    // dawn because the deepest value in a lit field is shadow on grass. Under one
    // blue source there is no second light to be green, and the rendered frame
    // came back a uniform desaturated green with no night in it at all. This is
    // the fattening hall's lesson a third time (`hedgerowLighting()` records the
    // second): the key has to win where it lands and the fill has to stay the
    // colour of everywhere it does not. Ambient takes its colour from here and the
    // toon ramp's shadow band tints toward it, so this one value is most of what
    // makes the field read as moonlit rather than as underexposed daylight.
    shadow: new THREE.Color(outside.fill).multiplyScalar(0.16).getHex(),
    // The night horizon is brighter than the overhead band, not a white fog wall.
    fog: new THREE.Color(outside.fill).multiplyScalar(0.46).getHex(),
    haze: 0.16,
    /**
     * **0.38, and inheriting it was a real defect.** `outside`'s own ceiling is
     * 0.62 — *"the only value above 0.35 in the game"*, which beat 7 earns by
     * being the first thing after the cull corridor's 0.08. Spread unchanged into
     * a night zone it put the bloom's saturation ceiling on the coldest scene in
     * the game, and nothing would have caught it: `main.ts` applies
     * `chromaForBeat` **only for beat 8** — beat 9 needs no override because the
     * wool-shed zone already carries the matching 0.30, and that is the pattern
     * this room now follows. An independent review found it.
     *
     * Derived rather than typed, because the wool shed's 0.30 and beat 9's arc row
     * are two statements of one fact that happen to agree, and this project's
     * recurring failure is the pair that stops agreeing. `COLOUR_ARC` names beat
     * 10 and this zone exists for beat 10 and nothing else.
     */
    chromaCeiling: nightChromaCeiling(),
  };
}

/** Beat 10's authored grade ceiling, from the one table that states it. */
function nightChromaCeiling(): number {
  const chroma = chromaForBeat(NIGHT_BEAT);
  if (chroma === null) throw new Error(`hedgerow: COLOUR_ARC has no beat ${String(NIGHT_BEAT)} row`);
  return chroma;
}

/**
 * The night sky's top band is darker than its fill and horizon by construction:
 * 0.12 → 0.30 → 0.46 of the one outside sky colour. Keeping that derivation here
 * prevents a later palette edit from leaving a painted-on midnight over new fog.
 *
 * It is anchored on `bloomZone.fill` rather than on the night zone's `key`, which
 * is the same colour taken down a further 0.45 for the moon. Chaining the two
 * would make every sky band move whenever the LIGHT was retuned, which is the
 * kind of coupling that produces a retune nobody can undo.
 */
export const NIGHT_ZENITH = new THREE.Color(bloomZone.fill).multiplyScalar(0.12).getHex();

/**
 * **0.2, and it is the number that stopped the field being white.**
   *
   * `fogDensity` is `haze × 0.45 × scale` and the shader's factor is
   * `1 − exp(−(density·z)²)` — quadratic, so it bites hard at the far end.
   * `lighting.ts` says in as many words that *"an outdoor zone should pass a
   * `fogScale` well below 1 rather than have its `haze` lowered"*, and the first
   * build of this room passed 1 anyway.
   *
   * What that looked like: at the `outside` zone's haze of 0.10 the density is
   * 0.045, so at 80 m `(0.045 × 80)² = 12.96` and the fog factor is **1.000**.
   * The road, the far hedge and all three trees were rendered as pure fog
   * colour — `0xcfe3ea`, a pale blue-white — and the post-bloom frame came back
   * with the entire far half of the field **blown out to white.** It read as a
   * blown exposure, which is the one reading this shot cannot afford, and no
   * gate can see it.
   *
   * 0.2 is derived rather than dialled. Density has to be about `0.833/z` for a
   * surface at `z` to be half dissolved, so for the road at ~90 m:
   * `0.833 / 90 = 0.00925`, and `0.00925 / (0.10 × 0.45) = 0.206` — which was the
 * first value, and the frames then showed the road and the far hedge still washing
 * out to the fog colour at 47 % coverage. **0.12 is the value a picture chose**:
 * half-dissolved at 154 m, so the road sits around 20 % and reads as a road.
   *
   * At 0.2 the far hedge (65 m) sits around 30 % dissolved, the road around
   * 44 %, and the near hedgerow at 20 m is barely touched — which is aerial
   * perspective, and which is what makes the haze lifting during the bloom
   * *hand something back* rather than reveal a white wall (rule 3).
   *
   * It applies to the pre-bloom yard palette too, where haze 0.45 becomes a
   * density of 0.042 and half-dissolves at 20 m. That is correct for the same
   * frame from the other side: at the fence, in the last of a storm, she cannot
   * see where she is going.
 *
 * ── It is exported because it had TWO statements and they disagreed ───────────
 *
 * `hedgerowLighting()` sets the room's fog at boot; `Bloom` re-derives it every
 * render frame from `BloomOptions.fogScale`, **which defaults to 1.** The first
 * wiring passed the room 0.2 and the bloom nothing, so the room's own haze was
 * silently overwritten with scale 1 on the first frame of the beat and the field
 * stayed white — and the bloom gate, which asserted a pre-bloom density of
 * 0.2025, *confirmed* the wrong number to four decimal places.
 *
 * That is the shape of defect this room already has a decision about (D-114): one
 * fact, one statement, and every reader imports it. There are two readers of this
 * one and `main.ts` hands the same constant to both.
 */
export const HEDGEROW_FOG_SCALE = 0.12;

function assetDefinitionOrThrow(id: string): AssetDefinition {
  const asset = getAsset(id);
  if (!asset) throw new Error(`hedgerow: asset "${id}" is not registered`);
  return asset;
}

function assetOrThrow(id: string): THREE.Group {
  return assetDefinitionOrThrow(id).create();
}

function box(
  name: string,
  half: HallSolid['half'],
  pos: HallSolid['pos'],
  colour: number,
  kind: HallSolid['kind'],
  visible = true,
  pitch?: number,
): HallSolid {
  return { name, half, pos, colour, kind, visible, ...(pitch === undefined ? {} : { pitch }) };
}

/** A solid slab whose upper face is the line between the two supplied terrain points. */
export function slope(
  name: string,
  minX: number,
  maxX: number,
  high: { z: number; y: number },
  low: { z: number; y: number },
  colour: number,
): HallSolid {
  const run = Math.abs(high.z - low.z);
  const drop = high.y - low.y;
  const angle = Math.atan2(drop, run);
  // local +Z maps to y = -sin(pitch). A surface higher at its local +Z end
  // therefore needs negative pitch; the caller orders high then low so the sign
  // follows their world-Z order rather than a second hand-authored convention.
  const pitch = high.z > low.z ? -angle : angle;
  const midY = (high.y + low.y) / 2;
  const midZ = (high.z + low.z) / 2;
  const normalY = Math.cos(pitch);
  const normalZ = Math.sin(pitch);
  return box(
    name,
    { x: (maxX - minX) / 2, y: GROUND_THICKNESS, z: Math.hypot(run, drop) / 2 },
    { x: (minX + maxX) / 2, y: midY - GROUND_THICKNESS * normalY, z: midZ - GROUND_THICKNESS * normalZ },
    colour,
    'organic',
    true,
    pitch,
  );
}

/** All drawn terrain and all physical boundaries, from one shared list. */
/** The embankment road's three solids, which beat 14's hill replaces with a cutting (`src/world/hill.ts`). */
export const HEDGEROW_ROAD_SOLIDS: readonly string[] = ['road-bank', 'road-surface', 'road-far-bank'];

export function hedgerowSolids(options: Pick<HedgerowOptions, 'road'> = {}): HedgerowSolid[] {
  const solids = allHedgerowSolids();
  return options.road === false ? solids.filter((solid) => !HEDGEROW_ROAD_SOLIDS.includes(solid.name)) : solids;
}

function allHedgerowSolids(): HedgerowSolid[] {
  const trailer = assetDefinitionOrThrow('box-trailer').footprint;
  const pallet = assetDefinitionOrThrow('pallet-stack').footprint;
  const bin = assetDefinitionOrThrow('wheeled-bin').footprint;
  const hedge = assetDefinitionOrThrow('hedge-section').footprint;
  const nearHedgeY = hedgerowGroundY(0, -20.9);
  // Bounds name the faces Rosalie meets. The side solids run beyond the north
  // solid's outward face so their corner has the same 0.5 m overlap as before;
  // keeping that overlap is what preserves the previous collider geometry while
  // moving only the public bound-line semantics.
  const sideNorthZ = HEDGEROW_BOUNDS.northZ + BOUND_HALF_THICKNESS + BOUND_SIDE_NORTH_OVERLAP;
  const sideSouthZ = HEDGEROW_BOUNDS.southZ - BOUND_SIDE_SOUTH_OVERLAP;
  const sideHalfZ = (sideNorthZ - sideSouthZ) / 2;
  const sideCenterZ = (sideNorthZ + sideSouthZ) / 2;
  const northHalfX = (HEDGEROW_BOUNDS.eastX - HEDGEROW_BOUNDS.westX) / 2 + BOUND_HALF_THICKNESS * 2;
  const solids: HedgerowSolid[] = [
    box('yard-apron', { x: 16, y: 0.1, z: 5.5 }, { x: -1, y: -0.1, z: 5.5 }, APRON, 'wet'),
    box('shed-wall', { x: 6, y: 1.7, z: 0.15 }, { x: -5, y: 1.7, z: 10.6 }, SHED_WALL, 'mineral'),
    slope('fall', -22, 22, { z: 0, y: hedgerowGroundY(0, 0) }, { z: -18, y: hedgerowGroundY(0, -18) }, EARTH),
    slope('ditch-near-bank', -22, 14, { z: -18, y: hedgerowGroundY(0, -18) }, { z: -18.5, y: hedgerowGroundY(0, -18.5) }, EARTH),
    box('ditch-bed', { x: 18, y: GROUND_THICKNESS, z: 0.7 }, { x: -4, y: hedgerowGroundY(0, -19.2) - GROUND_THICKNESS, z: -19.2 }, EARTH, 'organic'),
    slope('ditch-far-bank', -22, 14, { z: -20.4, y: hedgerowGroundY(0, -20.4) }, { z: -19.9, y: hedgerowGroundY(0, -19.9) }, EARTH),
    // The layout calls out only three pitched surfaces but also defines a 1.5%
    // field. Keeping this fourth slab pitched is required for visible ground,
    // grass and collider to stay on the same height function.
    slope('field', -28, 22, { z: -20.4, y: hedgerowGroundY(0, -20.4) }, { z: -86, y: hedgerowGroundY(0, -86) }, EARTH),
    // ── The road, and it took three solids to become visible ────────────────
    //
    // Act 2's first line is *"she is free, and the road is right there, and she
    // turns around"*, so the road has to be IN this beat's frames for that
    // sentence to exist — and it must never be reachable.
    //
    // `docs/hedgerow-layout.md` worked out that a surface at y = +1.6, z = −100
    // clears the 2.2 m far hedge by 0.63 m from her eye height, and it does. The
    // first build then made it a **0.2 m thick ribbon**, and the frame showed
    // exactly that: a pencil line above the hedge, with nothing under it. An
    // arithmetic clearance is not the same thing as a legible object.
    //
    // So it is three solids, and the bank is the one that does the work: 2.6 m of
    // raised ground carrying a 7 m carriageway, with a hedge line behind. What the
    // player reads at 75 m is a pale band lifted above the field — which is what a
    // road on an embankment looks like, and which is a *place* rather than a line.
    slope('road-bank', -40, 34, { z: -96, y: 1.5 }, { z: -90, y: hedgerowGroundY(0, -90) }, EARTH),
    box('road-surface', { x: 37, y: 0.35, z: 3.5 }, { x: -3, y: 1.25, z: -100 }, ROAD, 'mineral'),
    box('road-far-bank', { x: 37, y: 1.1, z: 2 }, { x: -3, y: 1.6, z: -104.5 }, EARTH, 'organic'),
    box('fence-west-run', { x: 11.5, y: FENCE_HEIGHT / 2, z: 0.05 }, { x: -9.5, y: FENCE_HEIGHT / 2, z: 0 }, STEEL, 'metal', false),
    // The timber bay's collider is deliberately NOT in this static list. `main.ts`
    // holds its slightly thicker collision half so `onBoardsGo` can remove the
    // exact collider together with the mesh swap. Keeping this old duplicate
    // here meant a successful speed check removed one wall while this second,
    // invisible wall still perched Rosalie on the fence.
    box('fence-east-run', { x: 8.5, y: FENCE_HEIGHT / 2, z: 0.05 }, { x: 12.5, y: FENCE_HEIGHT / 2, z: 0 }, STEEL, 'metal', false),
    // These are two runs because x -7…-5 is the omitted hedge section. One
    // continuous collider would make the visible exit a phantom opening.
    box('near-hedge-west-solid', { x: (-7 - (-20 - hedge.width / 2)) / 2, y: hedge.height / 2, z: hedge.length / 2 }, { x: (-7 + (-20 - hedge.width / 2)) / 2, y: nearHedgeY + hedge.height / 2, z: -20.9 }, EARTH, 'organic', false),
    box('near-hedge-east-solid', { x: ((12 + hedge.width / 2) - -5) / 2, y: hedge.height / 2, z: hedge.length / 2 }, { x: ((12 + hedge.width / 2) + -5) / 2, y: nearHedgeY + hedge.height / 2, z: -20.9 }, EARTH, 'organic', false),
    box('far-hedge-solid', { x: 28, y: hedge.height / 2, z: hedge.length / 2 }, { x: -3, y: hedgerowGroundY(-3, -86) + hedge.height / 2, z: -86 }, EARTH, 'organic', false),
    box('trailer-solid', { x: trailer.length / 2, y: (trailer.height - TRAILER_LOCAL.deckY) / 2, z: trailer.width / 2 }, { x: TRAILER_AT.x, y: TRAILER_LOCAL.deckY + (trailer.height - TRAILER_LOCAL.deckY) / 2, z: TRAILER_AT.z }, STEEL, 'metal', false),
    ...TRAILER_LOCAL.wheels.axleZ.flatMap((localZ, axleIndex) =>
      ([-TRAILER_LOCAL.wheels.centreX, TRAILER_LOCAL.wheels.centreX] as const).map((localX, sideIndex) =>
        box(
          `trailer-wheel-solid-${axleIndex + 1}-${sideIndex + 1}`,
          { x: TRAILER_LOCAL.wheels.radius, y: TRAILER_LOCAL.wheels.radius, z: TRAILER_LOCAL.wheels.width / 2 },
          { x: TRAILER_AT.x + localZ, y: TRAILER_LOCAL.wheels.radius, z: TRAILER_AT.z - localX },
          STEEL,
          'metal',
          false,
        ),
      ),
    ),
    ...([-TRAILER_LOCAL.legs.centreX, TRAILER_LOCAL.legs.centreX] as const).map((localX, index) =>
      box(
        `trailer-leg-solid-${index + 1}`,
        { x: TRAILER_LOCAL.legs.section / 2, y: TRAILER_LOCAL.legs.top / 2, z: TRAILER_LOCAL.legs.section / 2 },
        { x: TRAILER_AT.x + TRAILER_LOCAL.legs.z, y: TRAILER_LOCAL.legs.top / 2, z: TRAILER_AT.z - localX },
        STEEL,
        'metal',
        false,
      ),
    ),
    // ── The edge of the world, and until now there was not one ──────────────
    //
    // Every terrain slab above is authored to the extent its own content needs,
    // and no two of them agree: the apron is x −17…15 and stops dead at z = 11,
    // the fall is x −22…22, the ditch and its banks are x −22…**14**, the field
    // is x −28…22. Outside the union there is no floor at all and nothing that
    // stops her reaching it — so she walks off the map and falls forever.
    //
    // **That is not hypothetical.** She spawns under the trailer at z = 8.6,
    // 2.4 m from the apron's north edge, and the shed wall only covers x −11…1.
    // Alex walked out of the room on his first try; a probe from the spawn
    // reached y = −71 in twenty seconds of held input. `hedgerow-floor.spec.ts`
    // is the sweep that should have existed — the cull corridor has had one since
    // D-089, and this is the largest room in the game and had none.
    //
    // These three are invisible (`visible = false`, the same thing the fence runs
    // and the hedge solids do, and it costs zero draw calls — `solidMeshes`
    // skips them). The x pair is the NARROWEST floor of any band, so no band can
    // put her past its own slab: west is the apron's −17, east is the ditch's
    // +14. North closes the apron; south is `far-hedge-solid` at z = −86, which
    // already spans x −31…25 and is already a wall.
    box(
      'bound-west',
      { x: BOUND_HALF_THICKNESS, y: BOUND_HALF_HEIGHT, z: sideHalfZ },
      { x: HEDGEROW_BOUNDS.westX - BOUND_HALF_THICKNESS, y: 0, z: sideCenterZ },
      EARTH,
      'organic',
      false,
    ),
    box(
      'bound-east',
      { x: BOUND_HALF_THICKNESS, y: BOUND_HALF_HEIGHT, z: sideHalfZ },
      { x: HEDGEROW_BOUNDS.eastX + BOUND_HALF_THICKNESS, y: 0, z: sideCenterZ },
      EARTH,
      'organic',
      false,
    ),
    box(
      'bound-north',
      { x: northHalfX, y: BOUND_HALF_HEIGHT, z: BOUND_HALF_THICKNESS },
      { x: (HEDGEROW_BOUNDS.westX + HEDGEROW_BOUNDS.eastX) / 2, y: 0, z: HEDGEROW_BOUNDS.northZ + BOUND_HALF_THICKNESS },
      EARTH,
      'organic',
      false,
    ),
  ];

  for (const [index, at] of PALLET_STACKS.entries()) {
    solids.push(box(`pallet-stack-solid-${index + 1}`, { x: pallet.width / 2, y: pallet.height / 2, z: pallet.length / 2 }, { x: at.x, y: hedgerowGroundY(at.x, at.z) + pallet.height / 2, z: at.z }, STEEL, 'organic', false));
  }
  for (const [index, at] of BINS.entries()) {
    solids.push(box(`bin-solid-${index + 1}`, { x: bin.width / 2, y: bin.height / 2, z: bin.length / 2 }, { x: at.x, y: hedgerowGroundY(at.x, at.z) + bin.height / 2, z: at.z }, STEEL, 'metal', false));
  }
  // The trunks. Invisible, because the tree asset is already drawn — this is the
  // collision half of a thing that had only ever had the other one. See
  // `FIELD_TREES`. The westernmost is outside the room's own west bound and so
  // unreachable; it gets one anyway, because "every visible object is in both
  // lists" is a rule worth being able to state without an exception in it.
  for (const [index, at] of FIELD_TREES.entries()) {
    solids.push(box(`field-tree-solid-${index + 1}`, TRUNK_HALF, { x: at.x, y: hedgerowGroundY(at.x, at.z) + TRUNK_HALF.y, z: at.z }, EARTH, 'organic', false));
  }

  // ── The culvert, as six boxes with a hole between them ────────────────────
  //
  // **The bore must have no collider at all**, which is why this is not one box
  // with a note. Two shoulders, two jambs and a lintel leave the pipe open from
  // the channel bed up to its crown, so the one hole in the perimeter is a hole
  // in the collision as well — and `bound-west` at x −17 is what still stops her
  // about half a metre inside it. That is deliberate: beat 10 ends on
  // `CULVERT_ENTER_X` long before she reaches the back of the pipe, so nothing
  // beyond the perimeter has to exist, exactly as beat 9 part 1 does at its north
  // wall (D-160).
  //
  // **Six and not one, because the shoulders are lower than the centre.** The
  // asset steps its top edge so it is not a black rectangle at 64 px, and a
  // single full-height flank would put half a metre of invisible wall above each
  // end of the visible concrete — the drift this room already refuses for the
  // trees, the bins and both hedges. Every extent comes from `CULVERT_DIMENSIONS`,
  // which the ASSET exports.
  //
  // The protruding barrel is deliberately not in this list. It is round, it sits
  // on the bed, and the only part of it outside the jambs is a 70 mm sliver at
  // the springing line; a box around it would close part of the mouth.
  const culvert = CULVERT_DIMENSIONS;
  const culvertCrown = culvert.boreInvert + culvert.boreRadius * 2;
  const culvertJambHalfZ = (culvert.centreHalfWidth - culvert.boreRadius) / 2;
  for (const side of [-1, 1] as const) {
    solids.push(box(
      side < 0 ? 'culvert-shoulder-far' : 'culvert-shoulder-near',
      { x: culvert.halfThickness, y: culvert.shoulderHeight / 2, z: culvert.shoulderWidth / 2 },
      {
        x: CULVERT_AT.x,
        y: CULVERT_AT.y + culvert.shoulderHeight / 2,
        z: CULVERT_AT.z + side * (culvert.centreHalfWidth + culvert.shoulderWidth / 2),
      },
      EARTH,
      'mineral',
      false,
    ));
    solids.push(box(
      side < 0 ? 'culvert-jamb-far' : 'culvert-jamb-near',
      { x: culvert.halfThickness, y: culvert.wallHeight / 2, z: culvertJambHalfZ },
      {
        x: CULVERT_AT.x,
        y: CULVERT_AT.y + culvert.wallHeight / 2,
        z: CULVERT_AT.z + side * (culvert.boreRadius + culvertJambHalfZ),
      },
      EARTH,
      'mineral',
      false,
    ));
  }
  // From the bore's crown to the top of the wall. Rosalie is 0.32 m tall against
  // 0.70 m of clear opening, so she walks under this; the point of it existing is
  // that nothing taller does.
  const culvertLintelHalfY = (culvert.wallHeight - culvertCrown) / 2;
  solids.push(box(
    'culvert-lintel',
    { x: culvert.halfThickness, y: culvertLintelHalfY, z: culvert.boreRadius },
    {
      x: CULVERT_AT.x,
      y: CULVERT_AT.y + culvertCrown + culvertLintelHalfY,
      z: CULVERT_AT.z,
    },
    EARTH,
    'mineral',
    false,
  ));
  // The coping, which the first version of this block did not know about. She
  // cannot jump (D-017) and is 0.32 m tall, so nothing she can do reaches 1.10 m
  // — but *every visible object is in both lists* is the rule this room states
  // without an exception in it, and it already keeps that rule for a field tree
  // outside its own west bound. An independent review found the gap.
  solids.push(box(
    'culvert-coping',
    { x: culvert.copingHalfThickness, y: culvert.copingHeight / 2, z: culvert.copingHalfWidth },
    {
      x: CULVERT_AT.x,
      y: CULVERT_AT.y + culvert.wallHeight + culvert.copingHeight / 2,
      z: CULVERT_AT.z,
    },
    EARTH,
    'mineral',
    false,
  ));

  return solids;
}

export const PALLET_STACKS = [
  { x: -4.5, z: 6.2 },
  { x: 7.5, z: 5.4 },
] as const;
export const BINS = [
  { x: -9.2, z: 9.4 },
  { x: -7.9, z: 9.4 },
] as const;

/**
 * The exterior solids Holly routes around in beat 10, from registered footprints.
 *
 * These are the REAL footprints; `Companion`'s constructor inflates every one of
 * them by her body radius and refuses a station inside the result. That is why
 * `HOLLY_LIE_UP` sits at z 8.0 rather than 8.6 — see its note.
 *
 * **Two of the five are quarter-turned, and a footprint read straight through is
 * silently the wrong box.** `box-trailer` is placed along the yard's east-west
 * run, so its registered `length` (8 m) is its world-X extent and its `width`
 * (2.55 m) its world-Z extent; `hedgerowOccluders()` builds the same box the
 * same way and the two must agree. The culvert carries `CULVERT_AT.rotY = π/2`
 * because the asset's +Z is the approach face and maps to world +X, so its pipe
 * runs along X and its headwall spans Z. Nothing in the room complains about an
 * obstacle of the wrong shape; `DITCH_AT`'s header records what the identical
 * mistake cost in the cover list, where the volume it produced was empty.
 *
 * The trees are 40 m down the field and no authored route of hers goes near
 * them, so they are deliberately absent rather than forgotten.
 */
export function hedgerowCompanionObstacles(): CompanionObstacle[] {
  const footprintOf = (id: string): { width: number; length: number } => {
    const asset = getAsset(id);
    if (!asset) throw new Error(`hedgerow companion: asset "${id}" is not registered`);
    return asset.footprint;
  };
  /** `quarterTurned` swaps which registered extent runs along world X. See above. */
  const volume = (
    id: string,
    at: { x: number; z: number },
    quarterTurned = false,
  ): CompanionObstacle => {
    const footprint = footprintOf(id);
    const alongX = quarterTurned ? footprint.length : footprint.width;
    const alongZ = quarterTurned ? footprint.width : footprint.length;
    return {
      id,
      min: { x: at.x - alongX / 2, z: at.z - alongZ / 2 },
      max: { x: at.x + alongX / 2, z: at.z + alongZ / 2 },
    };
  };
  /**
   * The two field-fence runs, read off `hedgerowSolids()` rather than retyped.
   *
   * **They were absent until D-173**, which meant Holly walked through the one
   * wall in this room the player cannot — and nobody saw it, because until the
   * same decision the player could not get south of it to call her from there.
   *
   * `Companion` slides along an obstacle; it does not path around one. That is
   * survivable here only because the gap in this wall is a real 2 m hole at
   * x ∈ [2, 4] and `#moveToward` tries the X-only candidate first, so a Holly
   * pressed against the fence tracks along it toward whichever side Rosalie is
   * on and finds the opening. `HOLLY_LIE_UP` is on the field side precisely so
   * that this is a backstop and not the mechanism.
   */
  const fenceRuns = hedgerowSolids()
    .filter((solid) => solid.name === 'fence-west-run' || solid.name === 'fence-east-run')
    .map((solid) => ({
      id: solid.name,
      min: { x: solid.pos.x - solid.half.x, z: solid.pos.z - solid.half.z },
      max: { x: solid.pos.x + solid.half.x, z: solid.pos.z + solid.half.z },
    }));
  if (fenceRuns.length !== 2) {
    // A rename in `hedgerowSolids()` would otherwise silently hand Holly the
    // room back. This is the gate the paragraph above claims.
    throw new Error('hedgerow companion: both field-fence runs must be found by name');
  }
  const culvert = CULVERT_DIMENSIONS;
  return [
    ...fenceRuns,
    volume('box-trailer', { x: 3, z: 8.6 }, true),
    // Ids are per instance, not per asset: `Companion` rejects a duplicate id
    // and a room with two bins needs to be able to name one of them.
    ...PALLET_STACKS.map((at, index) => ({ ...volume('pallet-stack', at), id: `pallet-stack-${index + 1}` })),
    ...BINS.map((at, index) => ({ ...volume('wheeled-bin', at), id: `bin-${index + 1}` })),
    {
      id: 'culvert-headwall',
      min: { x: CULVERT_AT.x - culvert.halfBarrelLength, z: CULVERT_AT.z - culvert.wallWidth / 2 },
      max: { x: CULVERT_AT.x + culvert.halfBarrelLength, z: CULVERT_AT.z + culvert.wallWidth / 2 },
    },
  ];
}

/**
 * The three trees in the field, and they are dressing AND collision.
 *
 * **They had no colliders and Alex walked through one.** He reported *"some kind
 * of pipes which do not seem to have collision"*, which is what a 2.2 m leaning
 * trunk reads as at fifty metres in a dawn field, and he was right — this list
 * was inline in `dressHedgerow` and nothing in `hedgerowSolids` had ever heard of
 * it. Every other visible object in this room is in both lists: the trailer, the
 * pallet stacks, the bins, the fence, both hedges, the shed wall.
 *
 * It is one exported list now for the reason `testRoomSolids()` gives — *what you
 * see and what she stands on cannot drift* — which is a rule this room already
 * kept everywhere except here.
 */
export const FIELD_TREES = [
  { x: -24, z: -41 },
  { x: -14, z: -48 },
  { x: 9, z: -58 },
] as const;

/**
 * Half-extents of a trunk collider, metres.
 *
 * A box around a cylinder, sized at the height she actually meets it. The trunk
 * is a leaning cylinder whose radius tapers 0.46 → 0.20 over 2.08 m, so at her
 * 0.32 m height it is about 0.42; 0.38 is inside that and never sticks out past
 * the bark. The boughs are deliberately not collided — they start at 0.63 of
 * the trunk's length, which is 1.31 m up, and she is a third of a metre tall.
 */
const TRUNK_HALF = { x: 0.38, y: 1.1, z: 0.38 } as const;

const GRASS_ZONES = [
  { id: 'fall', minX: -12, maxX: 12, minZ: -18, maxZ: -1, count: 450, seed: 0x15a7 },
  { id: 'near-field', minX: -24, maxX: 18, minZ: -45, maxZ: -21, count: 800, seed: 0x28c4 },
  { id: 'far-field', minX: -28, maxX: 22, minZ: -84, maxZ: -45, count: 350, seed: 0x39d1 },
] as const;

/** Deterministic, authored-density dressing positions; exported for the room gate. */
export function hedgerowGrassPlacements(): Placement[] {
  const placements: Placement[] = [];
  for (const zoneDefinition of GRASS_ZONES) {
    const random = mulberry32(zoneDefinition.seed);
    const columns = Math.ceil(Math.sqrt(zoneDefinition.count * (zoneDefinition.maxX - zoneDefinition.minX) / (zoneDefinition.maxZ - zoneDefinition.minZ)));
    const rows = Math.ceil(zoneDefinition.count / columns);
    for (let index = 0; index < zoneDefinition.count; index += 1) {
      const column = index % columns;
      const row = Math.floor(index / columns);
      const x = zoneDefinition.minX + (column + 0.15 + random() * 0.7) / columns * (zoneDefinition.maxX - zoneDefinition.minX);
      const z = zoneDefinition.minZ + (row + 0.15 + random() * 0.7) / rows * (zoneDefinition.maxZ - zoneDefinition.minZ);
      placements.push({ x, y: hedgerowGroundY(x, z), z, rotY: random() * Math.PI * 2 });
    }
  }
  return placements;
}

/**
 * How many differently-seeded hedge sections there are.
 *
 * ── Why one was not enough, and the frame is what said so ────────────────────
 *
 * `hedge-section` is seeded, and the first build instanced **one** built group
 * along the whole run. Seeding an asset does nothing for that: `instanceGroup`
 * takes one source group and repeats its geometry, so all twenty-eight sections
 * of the far hedge had the identical silhouette, the identical lumps and the
 * identical bare branch in the identical place. In the frame it read as a **row
 * of pickets** — a manufactured object, forty metres long, in the one shot whose
 * whole job is that the world outside the farm is not manufactured.
 *
 * Three seeds is the smallest number that breaks the read: at three the eye
 * stops finding the period at a glance, and each variant is its own
 * `instanceGroup` call so the cost is three sets of the asset's three materials
 * rather than one. That is the whole trade, it is paid once for each of the two
 * hedge runs, and `render-budget.spec.ts` is what says whether it fits.
 *
 * **The mirror is free and it is doing half the work.** Alternate sections are
 * turned `rotY = π`, which flips a section end-for-end and gives six apparent
 * variants for the price of three — and it costs nothing at all, because a
 * placement's yaw is already baked per instance.
 */
const HEDGE_VARIANTS = 3;

/**
 * One placement list per variant, so a caller makes one `instanceGroup` call each.
 *
 * The variant a section gets is `|x| % HEDGE_VARIANTS`, and the mirror is
 * `(x / 2) % 2` — both derived from the position rather than sampled, because
 * D-045 requires the room to rebuild identically and a screenshot gate that
 * compares two runs is worthless if the hedge is different each time.
 */
function hedgePlacements(minX: number, maxX: number, z: number, gap = false): Placement[][] {
  const byVariant: Placement[][] = Array.from({ length: HEDGE_VARIANTS }, () => []);
  for (let x = minX; x <= maxX; x += 2) {
    if (gap && x === HEDGE_GAP_X) continue;
    const variant = Math.abs(x / 2) % HEDGE_VARIANTS;
    const list = byVariant[variant];
    if (list === undefined) continue;
    list.push({ x, y: hedgerowGroundY(x, z), z, rotY: Math.abs(x / 2) % 2 === 0 ? 0 : Math.PI });
  }
  return byVariant;
}

/** Add one hedge run: `HEDGE_VARIANTS` instanced groups, all sharing the run's name. */
function addHedgeRun(
  group: THREE.Group,
  name: string,
  minX: number,
  maxX: number,
  z: number,
  gap = false,
): void {
  const lists = hedgePlacements(minX, maxX, z, gap);
  for (const [variant, placements] of lists.entries()) {
    if (placements.length === 0) continue;
    // A distinct seed per variant, offset off the room's own so the near and far
    // runs draw from the same three shapes rather than six — the far hedge is at
    // 65 m and does not need shapes of its own.
    group.add(instanceGroup(
      buildHedgeSection({ seed: 191 + variant * 37 }),
      placements,
      `${name}-${String(variant)}`,
    ));
  }
}

function ditchPlacements(): Placement[] {
  const placements: Placement[] = [];
  for (let x = -20; x <= 12; x += 4) placements.push({ x, z: -19.2 });
  return placements;
}

function fencePlacements(): Placement[] {
  const placements: Placement[] = [];
  for (let x = -19; x <= 19; x += 2) {
    if (x === BOARDS_AT.x) continue;
    placements.push({ x, y: hedgerowGroundY(x, 0), z: 0, rotY: Math.PI / 2 });
  }
  return placements;
}

/**
 * The field fence, and it is **two** instanced runs rather than one bay.
 *
 * A bay carrying its own post and its own wires was the obvious build and it is
 * what shipped first. It looks wrong, and `west-field-edge-090.png` from that
 * build is the evidence: every bay sits level at its own ground height, so at a
 * 4 % fall each 4 m joint steps 16 cm and the three wires visibly break and
 * restart down the whole run. It read as a fence somebody had dropped, not a
 * fence on a slope.
 *
 * A fence on a slope has **upright posts and sloping wires**, and those are two
 * different transforms, so they have to be two instanced groups: the posts take
 * no pitch, the wires take the terrain's (`stockWirePlacements`). It costs one
 * extra draw call out of the 37 this room still has spare.
 *
 * It is deliberately not a registered kit asset — it is field-edge dressing, one
 * post and three box wires on the existing outside-earth surface. Boxes, not
 * cylinders: a cylinder spends twice the triangles drawing a line that is two
 * pixels wide from every authored field camera. The first attempt at this edge
 * used the farm's `perimeter-fence` for all three bounds and took the room from
 * 120 309 triangles to 269 829 against a 150 000 ceiling; see
 * `render-budget.spec.ts`.
 */
function stockFenceTimber(): THREE.Material {
  return surface('organic', EARTH);
}

function stockFencePart(geometry: THREE.BufferGeometry, name: string): THREE.Mesh {
  const mesh = new THREE.Mesh(geometry, stockFenceTimber());
  mesh.name = name;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

/** One upright post. Upright is the point: see the group comment above. */
function buildStockFencePost(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'hedgerow-stock-fence-post';
  const post = stockFencePart(new THREE.BoxGeometry(0.10, 1.40, 0.10), 'timber-post');
  post.position.y = 0.70;
  group.add(post);
  return group;
}

/**
 * The three wires of one span, authored level and pitched per placement.
 *
 * 5 % longer than the pitch so a pitched span still reaches its neighbours: a
 * span tilted by θ covers only `pitch × cos θ` horizontally, and the overlap
 * costs nothing while a gap would be a hole in the one thing this fence exists
 * to say. 5 % carries the run to about 18°, which covers the fall and the field;
 * the ditch is crossed by a single span whose average grade is far gentler than
 * its banks.
 */
function buildStockFenceWires(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'hedgerow-stock-fence-wires';
  for (const height of [0.35, 0.80, 1.25]) {
    const wire = stockFencePart(
      new THREE.BoxGeometry(0.018, 0.018, HEDGEROW_STOCK_FENCE_PITCH * 1.05),
      `wire-${height.toFixed(2)}`,
    );
    wire.position.y = height;
    group.add(wire);
  }
  return group;
}

/**
 * The pitch that makes a span meet its neighbours, from the height function.
 *
 * Measured across the whole span rather than at its centre, because the centre
 * derivative of a piecewise floor is meaningless exactly where it matters — the
 * ditch banks are two 1.2-grade segments half a metre long, and a span that
 * straddles them wants the chord, which is what a real wire does over a ditch.
 *
 * The sign: `instanceGroup` applies `rotX` in Euler `YXZ`, and a rotation of θ
 * about X sends the wire's local +Z to `(0, -sin θ, cos θ)`. The +Z end must
 * RISE by `rise`, so θ = -atan2(rise, span). Get this backwards and every span
 * tilts into the hill instead of along it, which a still frame of a gentle slope
 * will not show you.
 */
function stockFencePitch(x: number, z: number): number {
  const span = HEDGEROW_STOCK_FENCE_PITCH;
  const rise = hedgerowGroundY(x, z + span / 2) - hedgerowGroundY(x, z - span / 2);
  return -Math.atan2(rise, span);
}

/** Cover a straight field edge with full fence bays, including both end lines. */
function fenceRun(
  from: number,
  to: number,
  pitch: number,
  at: (along: number) => Placement,
): Placement[] {
  const direction = Math.sign(to - from);
  const count = Math.ceil(Math.abs(to - from) / pitch);
  const placements: Placement[] = [];
  for (let index = 0; index < count; index += 1) {
    const regular = from + direction * pitch * (index + 0.5);
    // Pin the final bay to the target line rather than letting a partial span
    // hang over a wall it is meant to meet. The tiny overlap with its neighbour
    // is invisible; a wire continuing over the shed wall is not.
    const terminal = to - direction * pitch / 2;
    const along = direction > 0 ? Math.min(regular, terminal) : Math.max(regular, terminal);
    placements.push(at(along));
  }
  return placements;
}

/**
 * The open timber field fence on the west and east bounds.
 *
 * This reads `HEDGEROW_BOUNDS`, the same inner-face statement as containment.
 * A final partial span overlaps its previous bay instead of crossing the terminal
 * line. The centred source's wires therefore reach both terminal lines while its
 * widely spaced posts sit entirely on terrain rather than in the east ditch void.
 */
function stockFencePlacements(): Placement[] {
  const side = (x: number): Placement[] => fenceRun(
    HEDGEROW_BOUNDS.southZ,
    HEDGEROW_BOUNDS.northZ,
    HEDGEROW_STOCK_FENCE_PITCH,
    (z) => ({ x, y: hedgerowGroundY(x, z), z }),
  );
  return [...side(HEDGEROW_BOUNDS.westX), ...side(HEDGEROW_BOUNDS.eastX)];
}

/**
 * The same spans as the posts, pitched to the ground they cross.
 *
 * Derived from `stockFencePlacements()` rather than written out again: the two
 * runs must stand at the same places or the wires leave their posts, and this is
 * the room where a second list of the same numbers has already cost a session.
 */
function stockWirePlacements(): Placement[] {
  return stockFencePlacements().map((placement) => ({
    ...placement,
    rotX: stockFencePitch(placement.x, placement.z),
  }));
}

/** The closed farm side keeps the same lattice and the same 2.44 m pitch. */
function northFencePlacements(): Placement[] {
  const pitch = assetDefinitionOrThrow('perimeter-fence').footprint.length;
  const north = (fromX: number, toX: number): Placement[] => fenceRun(
    fromX,
    toX,
    pitch,
    (x) => ({ x, y: hedgerowGroundY(x, HEDGEROW_BOUNDS.northZ), z: HEDGEROW_BOUNDS.northZ, rotY: Math.PI / 2 }),
  );
  return [
    ...north(HEDGEROW_BOUNDS.westX, -11),
    ...north(1, HEDGEROW_BOUNDS.eastX),
  ];
}

export function hedgerowLighting(): ZoneLightingOptions {
  return {
    // No fixtures: this exterior is lit by the dawn key and enormous sky only.
    //
    // **Proven to go red at one, session 28** (D-100). A single
    // `{ kind: 'point', position: { x: 0, y: 2, z: -10 }, intensity: 4, range: 8 }`
    // added here makes `render-budget.spec.ts` fail with *"hedgerow+dressed: more
    // point lights are visible than the room declares. Expected: <= 0, Received:
    // 1"*. A gate nobody has watched go red is not known to be a gate, and this
    // project has shipped that five times.
    fixtures: [],
    keyDirection: { x: -0.86, y: 0.28, z: -0.42 },
    /**
     * ── Warm key AGAINST cool fill, and the first balance had it backwards ─────
     *
     * 0.78 / 0.55 on the first build, and the post-bloom frame came back with a
     * blue sky over green grass and **no dawn in it at all**. The zone's key is
     * `0xffd27a`, dawn gold, and its fill is `0x8fc0d9`, a pale blue — at 0.78
     * against 0.55 the fill was most of the light on any surface not square to a
     * sun 17 degrees above the horizon, which outdoors is nearly every surface.
     * The gold was authored, present, and invisible.
     *
     * This is the fattening hall's lesson in the other hue. `palette.ts` records
     * that zone going amber-on-amber and reading as sepia, because *"warm light,
     * warm haze and amber-veiled distance leave nothing for the light to be warm
     * against"* — and the fix there was to put the cold into the shadows. Same
     * composition, stated in reverse: the key has to be able to win where it
     * lands, and the fill has to stay the colour of everywhere it does not.
     *
     * 0.92 / 0.46, and the exposure reference is their sum plus ambient so the
     * ramp's anchor moves with them (1.52). **The ratio is the decision, not the
     * levels** — if the field wants to be brighter overall, move
     * `exposureFrom`/`exposureTo` in `main.ts` and leave these two alone.
     */
    keyIntensity: 0.92,
    shadowExtent: 30,
    hemiIntensity: 0.46,
    ambientIntensity: 0.14,
    exposureReference: 1.52,
    fogScale: HEDGEROW_FOG_SCALE,
  };
}

/**
 * Beat 10's moon is LOW at +X/+Z, and both halves of that matter.
 *
 * **+X/+Z is the route decision.** Beat 10 travels −Z down the fall and then −X
 * along the channel, so the camera looks away from the moon on both legs.
 * `TOON_RAMP` has three soft bands and no rim term; a camera pointed at the key
 * photographs the unlit side of everything (`docs/traps.md`).
 *
 * **Low is the one that took a frame to find.** The first two builds put it at
 * 46° and the culvert came back with no form in it at all — at that elevation a
 * moon lights the field (normal +Y, dot 0.71) and the headwall's face (normal +X,
 * dot 0.63) to within a tenth of each other, so raising `keyIntensity` brightened
 * both and separated neither. At 20° the same two surfaces are 0.36 against 0.84,
 * two and a half to one, and the field's 1,600 vertical grass tufts catch the moon
 * against ground that does not. **A key that lands everywhere equally is a fill
 * with a shadow map.**
 *
 * ── The ratio is the decision, and it took three frames to get right ────────
 *
 * `hedgerowLighting()` records the same argument at dawn and this is its night
 * half. **A moon is a HARD light** — a small angular source on a clear night with
 * almost nothing bouncing back off a wet field — so the mix is nearly all key.
 *
 * The first build had it backwards: 0.30 key against 0.34 hemisphere and 0.10
 * ambient, which is a dawn's proportions with the sun turned down. The frame came
 * back legible, cold, and **modelled by nothing** — every surface in it landed
 * between 32 % and 61 % of the ramp, so the culvert's face, its shoulders, the
 * bank behind it and the water in front were one value with edge lines drawn on
 * top. Raising the key alone (0.40) did not fix it, because the fill rose with the
 * exposure that had to hold the level down.
 *
 * At 1.35 / 0.10 / 0.04 against a reference of 1.20, a surface square to the moon
 * runs off the top of the ramp — moonlit concrete is the brightest thing in a
 * field at night and should be — the ground sits near half of it, and a shadow
 * side falls to about a tenth. **That range is what a night has instead of
 * brightness**, and the three earlier attempts had no range at all.
 *
 * **`exposureReference` is the lever if a frame is too dark or too bright**, and
 * it is deliberately above the 0.83 sum — at the default it would expose the
 * night exactly as brightly as the dawn and only colder. Moving `keyIntensity`
 * alone changes how much of the light has a direction, which is a different
 * decision and the one this comment is about.
 */
export function hedgerowNightLighting(): ZoneLightingOptions {
  return {
    // **Proven to go red at one, session 46** (D-100). Adding
    // `{ kind: 'point', position: { x: 0, y: 2, z: -10 }, intensity: 4, range: 8 }`
    // makes the night census fail with *"hedgerow+night+bare: more point lights
    // are visible than the room declares. Expected: <= 0, Received: 1"*. A
    // no-fixture rule that has never failed is only a comment, and that is this
    // project's exact gate failure five times over.
    //
    // The message says **bare**, and the first version of this comment said
    // `dressed` — written from the arm that seemed likeliest rather than from the
    // run. The four arms are asserted in order and the bare one fails first. That
    // is the whole reason this experiment is worth the two minutes: a quoted
    // failure nobody has seen is a guess in quotation marks.
    fixtures: [],
    keyDirection: { x: 0.84, y: 0.36, z: 0.40 },
    keyIntensity: 1.35,
    shadowExtent: 30,
    hemiIntensity: 0.10,
    ambientIntensity: 0.04,
    exposureReference: 1.20,
    fogScale: HEDGEROW_FOG_SCALE,
  };
}

/**
 * How much rain is on the frame, 0..1. The yard's is 0.85.
 *
 * **The storm is going out, not gone.** Beat 7 opens under the trailer she reached
 * at the end of beat 6, so the weather cannot simply stop between the two — a cut
 * from a downpour to a clear sky would read as a different night. Half the yard's
 * density is a storm in its last twenty minutes.
 *
 * It is a **constant for the whole beat and the bloom does not touch it.** Rain
 * stopping and colour arriving at the same moment would be two events the player
 * reads as one, and the one that has to land is the colour.
 */
export const HEDGEROW_RAIN = 0.42;

/**
 * Positional ambience, and there is deliberately none.
 *
 * Everything continuous in this beat is a LOOP acquired in the render step rather
 * than a bed placed at boot — the rain, and `field-air` from the cut onward. That
 * is not a style preference: **a loop requested during `boot()` is permanently
 * silent.** `AudioEngine.play` hands back an inert `SilentHandle` until the audio
 * context is unlocked, and the click that unlocks it comes long after boot. Four
 * beats have now paid for that, most recently all three of beat 6's loops, and the
 * fix is to re-acquire on `!active` in the render step. See `main.ts`.
 */
export function hedgerowAmbience(): { id: string; at: { x: number; y: number; z: number }; gain: number }[] {
  return [];
}

export interface HedgerowParts {
  readonly group: THREE.Group;
  /** The intact timber bay; the beat owns its broken replacement. */
  readonly boards: THREE.Group;
  /** The baked destination sky; the bloom reveals this single texture. */
  readonly sky: Sky;
}

/**
 * The one opening in the field fence, in either state, placed at `BOARDS_AT`.
 * One builder, because beat 10 swaps the whole bay for this broken one at its
 * first frame (D-223) and must draw exactly what the room would have built.
 */
export function hedgerowBay(state: 'whole' | 'broken'): THREE.Group {
  return instanceGroup(
    buildFenceBoards(state),
    [{ ...BOARDS_AT, y: hedgerowGroundY(BOARDS_AT.x, BOARDS_AT.z) }],
    'hedgerow-boards',
  );
}

/** Static dressing only. Grass is deliberately one instanced field, not one batch per density zone. */
export function dressHedgerow(options: Pick<HedgerowOptions, 'night' | 'boards' | 'grassZ'> = {}): HedgerowParts {
  const group = new THREE.Group();
  group.name = 'hedgerow-dressing';
  group.add(instanceGroup(assetOrThrow('box-trailer'), [{ x: 3, y: hedgerowGroundY(3, 8.6), z: 8.6, rotY: Math.PI / 2 }], 'hedgerow-trailer'));
  group.add(instanceGroup(buildPalletStack(7), PALLET_STACKS.map((at) => ({ ...at, y: hedgerowGroundY(at.x, at.z) })), 'hedgerow-pallet-stacks'));
  group.add(instanceGroup(assetOrThrow('wheeled-bin'), BINS.map((at) => ({ ...at, y: hedgerowGroundY(at.x, at.z) })), 'hedgerow-bins'));
  group.add(instanceGroup(assetOrThrow('perimeter-fence'), fencePlacements(), 'hedgerow-fence'));
  group.add(instanceGroup(assetOrThrow('perimeter-fence'), northFencePlacements(), 'hedgerow-boundary-fence'));
  group.add(instanceGroup(buildStockFencePost(), stockFencePlacements(), 'hedgerow-boundary-stock-fence'));
  group.add(instanceGroup(buildStockFenceWires(), stockWirePlacements(), 'hedgerow-boundary-stock-wire'));
  const boards = hedgerowBay(options.boards ?? 'whole');
  group.add(boards);
  const ditch = instanceGroup(assetOrThrow('ditch-water'), ditchPlacements(), 'hedgerow-ditch-water');
  // Water is not ground contact: its authored datum is the documented 0.18 m
  // standing layer over the bed, so keep it as one group offset rather than a
  // second kind of per-placement floor height.
  ditch.position.y = DITCH_AT.waterY;
  group.add(ditch);
  addHedgeRun(group, 'hedgerow-near-hedge', -20, 12, -20.9, true);
  addHedgeRun(group, 'hedgerow-far-hedge', -30, 24, -86);
  group.add(instanceGroup(
    assetOrThrow('field-tree'),
    FIELD_TREES.map((at) => ({ ...at, y: hedgerowGroundY(at.x, at.z) })),
    'hedgerow-trees',
  ));
  // The assets' own authored organic colours carry the outside palette; this
  // room does not duplicate palette values with local colour constants.
  const band = options.grassZ;
  const grass = band === undefined ? hedgerowGrassPlacements() : hedgerowGrassPlacements().filter((at) => at.z > band.min && at.z < band.max);
  group.add(instanceGroup(assetOrThrow('grass-tuft'), grass, 'hedgerow-grass'));
  // One instance, and `instanceGroup` anyway: it is what every other placement in
  // this room uses, and a lone `create()` here would be the one object whose
  // transform is applied differently from its twenty-eight neighbours.
  group.add(instanceGroup(assetOrThrow('culvert-headwall'), [CULVERT_AT], 'hedgerow-culvert'));
  const skyZone = options.night === true ? hedgerowNightZone() : bloomZone;
  const zenith = options.night === true ? NIGHT_ZENITH : DAWN_ZENITH;
  return { group, boards, sky: createSky(skyZone, { seed: 7, zenith }) };
}

/** Build the un-wired room; the integrator owns lighting, physics and bloom timing. */
export interface HedgerowOptions {
  /**
   * Bake the moonlit sky for Beat 10. This stays on room construction because a
   * lighting-only switch leaves the daytime texture behind every opening — a
   * failure the budget census cannot see because a background has zero calls.
   */
  readonly night?: boolean;
  /**
   * Which state the one opening in the field fence is built in. Default `whole`.
   *
   * ── Why this is an option and not a beat-7 side effect ──────────────────────
   *
   * Beat 7 breaks these boards at runtime and swaps the mesh in `onBoardsGo`.
   * Beat 10 is a LATER NIGHT IN THE SAME FIELD, so the honest state of the world
   * when it opens is the hole she already made — and until D-173 it was not:
   * beat 10 built the bay whole, kept its collider, and **sealed the player away
   * from the entire beat.** From `HEDGEROW_NIGHT_SPAWN`, holding forward stopped
   * her at z = 0.19 and stayed there; rolling the bay at the cap stopped her at
   * z = 0.26. The twelve, the dog, Holly's call downfield and the culvert are all
   * south of this line and none of them was reachable. Every spec that appeared
   * to play the beat had teleported past it with `respawn`.
   *
   * `beat-10-gap.spec.ts` is the delta that would go red: the same held key from
   * the same spawn, `?beat=10` against the room's own `?room=hedgerow-night`,
   * and the two must disagree about whether she reaches the field.
   *
   * **Since session 80 beat 10 no longer passes it**: both hedgerow room modules
   * build the bay whole and beat 10's part swaps in `hedgerowBay('broken')` at
   * `create` (D-223). The hill is the caller that still builds it broken.
   *
   * **It is deliberately not keyed on `night`.** `?room=hedgerow-night` is the
   * control boot the census and the shot runs use, and a control that silently
   * changed one of its meshes would stop being a control — the same argument the
   * herd's own beat gate makes in `main.ts`.
   */
  readonly boards?: 'whole' | 'broken';
  /**
   * Add the dressing. **Only `render-budget.spec.ts` ever passes `false`**, and it
   * is why this option exists at all.
   *
   * D-100: *where a gate measures staged content, assert a DELTA* — one census with
   * the thing absent, one with it present, everything else held still. **A level is
   * also what an empty room reports**, and a field of 1,600 instanced grass tufts
   * is exactly the content that reports a plausible triangle count when it has
   * silently failed to draw. The two censuses have to come from one camera in one
   * room, so the only honest control is this room minus its dressing.
   *
   * The alternative was to census the dressed room from two framings and subtract,
   * which is the defect session 24 shipped: a real number and a false claim.
   *
   * The bare terrain is still a complete, walkable room — the shell, the boards and
   * the sky. Nothing about the geometry she stands on changes, which is what makes
   * the difference attributable to the dressing alone.
   */
  readonly dressing?: boolean;
  /**
   * Build the embankment road. Default true. Beat 14's hill is the one caller
   * that passes `false`: from the knoll's crest the road has to be BELOW her
   * (spec §7.14), and the embankment puts it 3.4 m above the far field, so the
   * hill lays its own road in a cutting instead. Visual and collider lists
   * both read this, through `hedgerowSolids`.
   */
  readonly road?: boolean;
  /**
   * Plant the field's grass only between these z. Beat 14's hill passes the
   * band its lenses can resolve: north of the knoll's foot the tufts would
   * stand under the knoll, and south of z −32 they are 50–90 m from every hill
   * shot, sub-pixel in the haze and still submitted twice (colour and shadow)
   * — the turn's census was 40 000 triangles over the ceiling on them.
   */
  readonly grassZ?: { readonly min: number; readonly max: number };
}

export function createHedgerow(options: HedgerowOptions = {}): HedgerowParts {
  const room = new THREE.Group();
  room.name = 'hedgerow';
  room.add(solidMeshes(hedgerowSolids(options), 'hedgerow-shell'));
  const dressing = dressHedgerow(options);
  if (options.dressing === false) {
    // Everything except the boards, which are the beat's own object and are held
    // in `HedgerowParts` for the mesh swap. Disposing them here would leave the
    // director holding a group that is not in the scene.
    for (const child of [...dressing.group.children]) {
      if (child !== dressing.boards) child.removeFromParent();
    }
  }
  room.add(dressing.group);
  return { group: room, boards: dressing.boards, sky: dressing.sky };
}
